'use client';
// Investor Hub — everything a trader or investor needs to evaluate the platform
import { useMemo, useState } from 'react';
import {
  Atom, CheckCircle2, CircleDashed, Clock3, Flame, Gem, Landmark,
  LineChart, Map, ShieldCheck, Sparkles, TrendingUp, Vault,
} from 'lucide-react';
import { fetchJson, usePoll, fmtAmt, fmtInt, SectionHeader, Spinner } from './shared';
import { AreaChart, DonutGauge } from './charts';
import type { InvestorData, TierT, Net } from './types';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';

const money = (n: number) => '$' + new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
const TONE: Record<string, string> = {
  cyan: 'text-cyan-300 bg-cyan-500/10 ring-cyan-400/30',
  rose: 'text-rose-400 bg-rose-500/10 ring-rose-500/25',
  teal: 'text-teal-400 bg-teal-500/10 ring-teal-500/25',
  emerald: 'text-emerald-400 bg-emerald-500/10 ring-emerald-500/25',
  violet: 'text-violet-400 bg-violet-500/10 ring-violet-500/25',
  amber: 'text-amber-400 bg-amber-500/10 ring-amber-500/25',
};
const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  atom: Atom, flame: Flame, vault: Vault, chart: LineChart, gem: Gem, shield: ShieldCheck,
};

function fmtEta(sec: number): string {
  if (!sec || sec < 0) return '—';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ---------- Yield calculator ----------
function YieldCalculator({ tiers, price }: { tiers: TierT[]; price: number }) {
  const [amount, setAmount] = useState('1000');
  const [tierId, setTierId] = useState('lock365');
  const [months, setMonths] = useState(12);
  const amt = Math.max(0, Number(amount) || 0);
  const tier = tiers.find(t => t.id === tierId) ?? tiers[0];

  const { series, finalBal, profit } = useMemo(() => {
    if (!tier || amt <= 0) return { series: [amt || 0, amt || 0], finalBal: amt, profit: 0 };
    const monthly = tier.apr / 12;
    const pts: number[] = [];
    let bal = amt;
    pts.push(bal);
    for (let i = 0; i < months; i++) { bal += bal * monthly; pts.push(bal); }
    return { series: pts, finalBal: bal, profit: bal - amt };
  }, [tier, amt, months]);

  return (
    <div className="glass p-5 space-y-4">
      <div className="text-sm font-bold flex items-center gap-2"><TrendingUp className="size-4 text-teal-400" /> Compound yield calculator</div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-bold mb-1.5 block" htmlFor="yc-amount">Amount (AMAN)</label>
          <Input id="yc-amount" type="number" min="0" step="any" value={amount} onChange={e => setAmount(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-bold mb-1.5 block" htmlFor="yc-tier">Plan</label>
          <select
            id="yc-tier"
            value={tierId} onChange={e => setTierId(e.target.value)}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
          >
            {tiers.map(t => <option key={t.id} value={t.id}>{t.label} — {(t.apr * 100).toFixed(0)}%</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold mb-1.5 block">Duration: <b className="text-teal-400">{months} months</b></label>
          <input type="range" min={1} max={36} value={months} onChange={e => setMonths(Number(e.target.value))} className="w-full accent-teal-500 mt-2" aria-label="Duration in months" />
        </div>
      </div>
      <AreaChart series={series} height={150} color="#2dd4bf" />
      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <div className="rounded-lg bg-muted/40 ring-1 ring-border p-2.5">
          <div className="text-[0.62rem] text-muted-foreground">Capital</div>
          <div className="font-black" dir="ltr">{fmtAmt(amt)} AMAN</div>
        </div>
        <div className="rounded-lg bg-teal-500/10 ring-1 ring-teal-500/25 p-2.5">
          <div className="text-[0.62rem] text-muted-foreground">Projected balance</div>
          <div className="font-black text-teal-400" dir="ltr">{fmtAmt(finalBal)} AMAN</div>
        </div>
        <div className="rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/25 p-2.5">
          <div className="text-[0.62rem] text-muted-foreground">Projected profit</div>
          <div className="font-black text-emerald-400" dir="ltr">{fmtAmt(profit)} <span className="text-[0.6rem]">(~{money(profit * price)})</span></div>
        </div>
      </div>
      <p className="text-[0.65rem] text-muted-foreground">Yield accrues every second on-chain (not monthly) — the chart above is a monthly compounding approximation for illustration. Dollar values use the current price of {price ? `$${price.toFixed(4)}` : '—'} and may change.</p>
    </div>
  );
}

export function InvestorHub({ net }: { net: Net }) {
  const { data } = usePoll<InvestorData>(async () => fetchJson('/api/investor'), 5000);
  const d = data;

  return (
    <div className="space-y-6">
      {/* Investor hero */}
      <section className="relative overflow-hidden rounded-2xl border border-border glass">
        <div className="absolute inset-0 grid-bg" />
        <div className="relative p-6 md:p-8">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 ring-1 ring-amber-500/30 px-3 py-1 text-xs font-bold text-amber-400">
              <Sparkles className="size-3.5" /> Investor hub
            </span>
            {d && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 ring-1 ring-emerald-500/30 px-3 py-1 text-xs font-bold text-emerald-400" dir="ltr">
                {d.hasMarket ? `AMAN $${d.price.toFixed(4)}` : 'AMM ready — deploy the first pool in the DEX'}
              </span>
            )}
          </div>
          <h1 className="text-2xl md:text-4xl font-black leading-tight">
            <span className="grad-text">AMAN</span> — an asset engineered for what comes next
          </h1>
          <p className="text-sm text-muted-foreground max-w-3xl mt-3 leading-relaxed">
            A hard-capped, transparent supply. A deflationary burn active on every transaction. Staking yields up to 15%
            with time locks. An on-chain AMM where the price is set by real reserves. And a standardized post-quantum
            security layer (NIST FIPS 204) protecting every transaction from genesis.
            <b className="text-foreground"> Everything an investor in this decade should demand — in a single platform.</b>
          </p>
          {d && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              <div className="rounded-xl bg-muted/40 ring-1 ring-border p-3.5">
                <div className="text-[0.65rem] text-muted-foreground">Market cap</div>
                <div className="text-lg font-black text-emerald-400" dir="ltr">{d.hasMarket ? money(d.mcap) : '—'}</div>
              </div>
              <div className="rounded-xl bg-muted/40 ring-1 ring-border p-3.5">
                <div className="text-[0.65rem] text-muted-foreground">Staked value (TVL)</div>
                <div className="text-lg font-black text-teal-400" dir="ltr">{d.hasMarket ? money(d.tvl) : '—'}</div>
              </div>
              <div className="rounded-xl bg-muted/40 ring-1 ring-border p-3.5">
                <div className="text-[0.65rem] text-muted-foreground">Staked ratio of supply</div>
                <div className="text-lg font-black text-amber-400" dir="ltr">{(d.tokenomics.stakeRatio * 100).toFixed(2)}%</div>
              </div>
              <div className="rounded-xl bg-muted/40 ring-1 ring-border p-3.5">
                <div className="text-[0.65rem] text-muted-foreground">Global-market readiness</div>
                <div className="text-lg font-black text-cyan-300" dir="ltr">{d.readiness}%</div>
              </div>
            </div>
          )}
        </div>
      </section>

      {!d && <Spinner label="Preparing investment data…" />}

      {d && (
        <>
          {/* Why AMAN */}
          <div>
            <SectionHeader icon={Sparkles} title="Why investors choose AMAN" desc="Six reasons with live numbers from the chain — not marketing promises" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {d.valueProps.map(p => {
                const Icon = ICONS[p.icon] ?? Sparkles;
                return (
                  <div key={p.title} className="glass glass-hover p-5">
                    <div className={`rounded-xl ring-1 p-2.5 w-fit mb-3 ${TONE[p.tone] ?? TONE.emerald}`}>
                      <Icon className="size-5" />
                    </div>
                    <div className="text-xs text-muted-foreground">{p.title}</div>
                    <div className="text-2xl font-black mt-0.5" dir="ltr">{p.value}</div>
                    <p className="text-[0.68rem] text-muted-foreground mt-1.5 leading-relaxed">{p.sub}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tokenomics */}
          <div>
            <SectionHeader icon={Gem} title="Tokenomics — a transparent, verifiable economy" desc="Every number below is read directly from the chain's blocks — anyone can verify it in the Explorer" />
            <div className="grid lg:grid-cols-3 gap-4">
              <div className="glass p-5 flex flex-col items-center justify-center gap-3">
                <DonutGauge pct={d.tokenomics.mintedPct} label="of the hard cap" color="#f0b429" />
                <div className="text-center text-[0.68rem] text-muted-foreground">
                  {fmtAmt(d.tokenomics.totalCreated)} of {fmtInt(d.tokenomics.maxSupply)} AMAN minted — the rest is distributed exclusively through decreasing mining rewards
                </div>
              </div>
              <div className="glass p-5 flex flex-col items-center justify-center gap-3">
                <DonutGauge pct={Math.min(100, d.tokenomics.burnedPct)} label="of all minted is burned" color="#fb7185" />
                <div className="text-center text-[0.68rem] text-muted-foreground">
                  {fmtAmt(d.tokenomics.totalBurned)} AMAN permanently destroyed — burning accelerates as network activity grows
                </div>
              </div>
              <div className="glass p-5 space-y-2.5 text-xs">
                {[
                  { l: 'Current reward per block', v: `${fmtAmt(d.tokenomics.blockReward)} AMAN`, c: 'text-amber-400' },
                  { l: 'Next halving in', v: `${fmtInt(d.tokenomics.nextHalvingBlocks)} blocks`, c: 'text-foreground' },
                  { l: 'Estimated halving time', v: fmtEta(d.tokenomics.halvingEtaSec), c: 'text-foreground' },
                  { l: 'Average block time', v: `${d.tokenomics.avgBlockSec.toFixed(1)} s`, c: 'text-emerald-400' },
                  { l: 'Circulating supply', v: `${fmtAmt(d.tokenomics.circulating)} AMAN`, c: 'text-emerald-400' },
                  { l: 'Tokens deployed / holders', v: `${fmtInt(d.tokenomics.tokens)} / ${fmtInt(d.tokenomics.tokenHolders)}`, c: 'text-violet-400' },
                ].map(r => (
                  <div key={r.l} className="flex items-center justify-between border-b border-border/50 pb-2 last:border-0">
                    <span className="text-muted-foreground">{r.l}</span>
                    <span className={`font-bold ${r.c}`} dir="ltr">{r.v}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Emission curve */}
            <div className="glass p-5 mt-4">
              <div className="text-sm font-bold mb-2 flex items-center gap-2"><LineChart className="size-4 text-amber-400" /> Cumulative emission curve — eight halving eras</div>
              <AreaChart
                series={[...d.emission.map(e => e.cumulative), d.emission[d.emission.length - 1].cumulative]}
                capRatio={d.tokenomics.maxSupply}
                capLabel="Absolute cap 100M"
                color="#f0b429"
                height={170}
              />
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5 mt-3 text-center text-[0.6rem]">
                {d.emission.map(e => (
                  <div key={e.era} className="rounded-md bg-muted/40 ring-1 ring-border p-1.5">
                    <div className="text-muted-foreground">Era {e.era}</div>
                    <div className="font-black text-amber-400" dir="ltr">{e.reward} AMAN</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Yields */}
          <div>
            <SectionHeader icon={Vault} title="Term-deposit style yields — up to 15% APR" desc="Three plans with time locks: yield comes from the chain protocol itself and accrues every second" />
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="space-y-3">
                {d.staking.tiers.map((t, i) => (
                  <div key={t.id} className={`glass p-4 flex items-center gap-4 ${i === 2 ? 'ring-1 ring-teal-500/30' : ''}`}>
                    <div className="rounded-xl bg-teal-500/10 ring-1 ring-teal-500/25 text-teal-400 p-3 text-center shrink-0">
                      <div className="text-xl font-black" dir="ltr">{(t.apr * 100).toFixed(0)}%</div>
                      <div className="text-[0.55rem]">APR</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm flex items-center gap-2 flex-wrap">
                        {t.label}
                        {t.lockDays > 0 && <span className="inline-flex items-center gap-1 text-[0.6rem] rounded-full bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/25 px-2 py-0.5"><Clock3 className="size-3" /> {t.lockDays}-day lock</span>}
                        {i === 2 && <span className="text-[0.6rem] rounded-full bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/25 px-2 py-0.5">highest yield</span>}
                      </div>
                      <div className="text-[0.65rem] text-muted-foreground mt-1">minimum {fmtInt(t.minStake)} AMAN · currently staked {fmtAmt(t.staked)} AMAN</div>
                    </div>
                  </div>
                ))}
                <div className="rounded-xl bg-teal-500/5 ring-1 ring-teal-500/20 p-4 text-[0.68rem] text-muted-foreground leading-relaxed">
                  <b className="text-teal-400">Total staked:</b> {fmtAmt(d.staking.totalStaked)} AMAN · <b className="text-teal-400">locked in term deposits:</b> {fmtAmt(d.staking.lockedTotal)} · network average yield {(d.staking.avgApr * 100).toFixed(2)}% · locking reduces sell pressure — a model familiar to any traditional investor.
                </div>
              </div>
              <YieldCalculator tiers={d.staking.tiers} price={d.price} />
            </div>
          </div>

          {/* Institutional readiness */}
          <div>
            <SectionHeader icon={Landmark} title="Global financial-market requirements" desc={`An institutional checklist — ${d.readiness}% complete, the rest on the roadmap`} />
            <div className="glass p-5 mb-3">
              <Progress value={d.readiness} className="h-2.5" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {d.institutional.map(it => (
                <div key={it.title} className="glass p-4 flex gap-3">
                  {it.done
                    ? <CheckCircle2 className="size-5 text-emerald-400 shrink-0 mt-0.5" />
                    : <CircleDashed className="size-5 text-muted-foreground shrink-0 mt-0.5" />}
                  <div className="min-w-0">
                    <div className="text-sm font-bold flex items-center gap-2 flex-wrap">
                      {it.title}
                      <span className={`text-[0.58rem] rounded-full px-2 py-0.5 ring-1 ${it.done ? 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/25' : 'bg-muted text-muted-foreground ring-border'}`}>{it.status}</span>
                    </div>
                    <p className="text-[0.68rem] text-muted-foreground mt-1 leading-relaxed">{it.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Roadmap */}
          <div>
            <SectionHeader icon={Map} title="Roadmap" desc="Four phases from launch to institutional expansion" />
            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
              {d.roadmap.map((r, i) => (
                <div key={r.phase} className={`glass p-4 relative overflow-hidden ${r.pct === 100 ? 'border-emerald-500/30' : ''}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[0.6rem] font-black text-muted-foreground" dir="ltr">0{i + 1}</span>
                    <span className={`text-[0.58rem] rounded-full px-2 py-0.5 ring-1 ${r.pct === 100 ? 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/25' : r.pct > 0 ? 'bg-amber-500/10 text-amber-400 ring-amber-500/25' : 'bg-muted text-muted-foreground ring-border'}`}>{r.state}</span>
                  </div>
                  <div className="font-bold text-sm mb-2">{r.phase}</div>
                  <ul className="space-y-1.5">
                    {r.items.map(it => (
                      <li key={it} className="text-[0.66rem] text-muted-foreground flex items-start gap-1.5">
                        <span className={`mt-1 size-1.5 rounded-full shrink-0 ${r.pct === 100 ? 'bg-emerald-400' : 'bg-muted-foreground/50'}`} />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <p className="text-[0.68rem] text-muted-foreground text-center max-w-2xl mx-auto leading-relaxed">
            {d.disclaimer}
          </p>
        </>
      )}
    </div>
  );
}
